import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { gsap } from 'gsap';
import './CardNav.css';

export type CardNavLink = {
  label: string;
  href?: string;
  ariaLabel?: string;
  onClick?: () => void;
};

export type CardNavItem = {
  label: string;
  bgColor: string;
  textColor: string;
  links?: CardNavLink[];
  onSelect?: () => void;
};

export interface CardNavProps {
  logo: string;
  logoAlt?: string;
  logoHref?: string;
  onLogoClick?: (e: React.MouseEvent) => void;
  items: CardNavItem[];
  className?: string;
  layout?: 'floating' | 'header' | 'inline';
  ease?: string;
  baseColor?: string;
  menuColor?: string;
  buttonBgColor?: string;
  buttonTextColor?: string;
  ctaLabel?: string;
  onCtaClick?: () => void;
  hideCta?: boolean;
  /** Keep menu expanded (e.g. Affordability calculator picker) */
  persistExpanded?: boolean;
  /** Highlight a card by index (select mode) */
  activeIndex?: number;
  selectMode?: boolean;
  topBarExtra?: React.ReactNode;
  /** Hide logo + top bar (inline calculator picker) */
  hideTopBar?: boolean;
}

const ANIM_DURATION = 0.26;
const ANIM_STAGGER = 0.05;

const ArrowIcon = () => (
  <svg
    className="nav-card-link-icon"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M7 17L17 7" />
    <path d="M7 7h10v10" />
  </svg>
);

const CardNav: React.FC<CardNavProps> = ({
  logo,
  logoAlt = 'Logo',
  logoHref,
  onLogoClick,
  items,
  className = '',
  layout = 'floating',
  ease = 'power3.out',
  baseColor = '#fff',
  menuColor,
  buttonBgColor,
  buttonTextColor,
  ctaLabel = 'Get Started',
  onCtaClick,
  hideCta = false,
  persistExpanded = false,
  activeIndex,
  selectMode = false,
  topBarExtra,
  hideTopBar = false
}) => {
  const [isExpanded, setIsExpanded] = useState(persistExpanded);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const navRef = useRef<HTMLDivElement | null>(null);
  const cardsRef = useRef<HTMLDivElement[]>([]);
  const tlRef = useRef<gsap.core.Timeline | null>(null);

  const calculateHeight = useCallback(() => {
    const navEl = navRef.current;
    if (!navEl) return hideTopBar ? 120 : persistExpanded ? 200 : 260;

    if (hideTopBar) {
      const contentEl = navEl.querySelector('.card-nav-content') as HTMLElement;
      return contentEl ? contentEl.scrollHeight + 24 : 120;
    }

    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    if (isMobile) {
      const contentEl = navEl.querySelector('.card-nav-content') as HTMLElement;
      if (contentEl) {
        const wasVisible = contentEl.style.visibility;
        const wasPointerEvents = contentEl.style.pointerEvents;
        const wasPosition = contentEl.style.position;
        const wasHeight = contentEl.style.height;

        contentEl.style.visibility = 'visible';
        contentEl.style.pointerEvents = 'auto';
        contentEl.style.position = 'static';
        contentEl.style.height = 'auto';

        void contentEl.offsetHeight;

        const topBar = 60;
        const padding = 16;
        const contentHeight = contentEl.scrollHeight;

        contentEl.style.visibility = wasVisible;
        contentEl.style.pointerEvents = wasPointerEvents;
        contentEl.style.position = wasPosition;
        contentEl.style.height = wasHeight;

        return topBar + contentHeight + padding;
      }
    }
    return persistExpanded ? 200 : 260;
  }, [persistExpanded, hideTopBar]);

  const createTimeline = useCallback(() => {
    const navEl = navRef.current;
    if (!navEl) return null;

    if (persistExpanded || hideTopBar) {
      const h = calculateHeight();
      gsap.set(navEl, { height: h, overflow: 'hidden' });
      gsap.set(cardsRef.current, { y: 0, opacity: 1 });
      return null;
    }

    gsap.set(navEl, { height: 60, overflow: 'hidden' });
    gsap.set(cardsRef.current, { y: 50, opacity: 0 });

    const tl = gsap.timeline({ paused: true });

    tl.to(navEl, {
      height: calculateHeight,
      duration: ANIM_DURATION,
      ease
    });

    tl.to(
      cardsRef.current,
      { y: 0, opacity: 1, duration: ANIM_DURATION, ease, stagger: ANIM_STAGGER },
      '-=0.12'
    );

    return tl;
  }, [calculateHeight, ease, persistExpanded, hideTopBar]);

  useLayoutEffect(() => {
    if (persistExpanded || hideTopBar) {
      const navEl = navRef.current;
      if (navEl) {
        gsap.set(navEl, { height: calculateHeight(), overflow: 'hidden' });
        gsap.set(cardsRef.current, { y: 0, opacity: 1 });
      }
      setIsExpanded(true);
      return;
    }

    setIsExpanded(false);

    const tl = createTimeline();
    tlRef.current = tl;

    return () => {
      tl?.kill();
      tlRef.current = null;
    };
  }, [ease, items, layout, persistExpanded, hideTopBar, createTimeline, calculateHeight]);

  useLayoutEffect(() => {
    if (persistExpanded || hideTopBar) return;

    const handleResize = () => {
      if (!tlRef.current) return;

      if (isExpanded) {
        const newHeight = calculateHeight();
        gsap.set(navRef.current, { height: newHeight });

        tlRef.current.kill();
        const newTl = createTimeline();
        if (newTl) {
          newTl.progress(1);
          tlRef.current = newTl;
        }
      } else {
        tlRef.current.kill();
        const newTl = createTimeline();
        if (newTl) {
          tlRef.current = newTl;
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isExpanded, persistExpanded, calculateHeight, createTimeline]);

  const closeMenu = useCallback(() => {
    const tl = tlRef.current;
    if (!tl || !isExpanded) return;
    tl.eventCallback('onReverseComplete', null);
    tl.eventCallback('onReverseComplete', () => setIsExpanded(false));
    tl.reverse();
  }, [isExpanded]);

  useEffect(() => {
    if (persistExpanded || hideTopBar || !isExpanded) return;

    const handlePointerDown = (event: MouseEvent) => {
      const root = containerRef.current;
      if (!root || root.contains(event.target as Node)) return;
      closeMenu();
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isExpanded, persistExpanded, hideTopBar, closeMenu]);

  const toggleMenu = () => {
    if (persistExpanded || hideTopBar) return;
    const tl = tlRef.current;
    if (!tl) return;
    if (!isExpanded) {
      setIsExpanded(true);
      tl.eventCallback('onReverseComplete', null);
      tl.pause(0);
      tl.play(0);
    } else {
      closeMenu();
    }
  };

  const setCardRef = (i: number) => (el: HTMLDivElement | null) => {
    if (el) cardsRef.current[i] = el;
  };

  const handleCardClick = (idx: number, item: CardNavItem) => {
    if (selectMode && item.onSelect) {
      item.onSelect();
      return;
    }
    if (!persistExpanded && !isExpanded) {
      toggleMenu();
    }
  };

  const handleLinkClick = (e: React.MouseEvent, link: CardNavLink) => {
    if (link.onClick) {
      e.preventDefault();
      link.onClick();
      if (!persistExpanded && isExpanded) {
        closeMenu();
      }
    }
  };

  const layoutClass =
    layout === 'header'
      ? 'card-nav-container--header'
      : layout === 'inline'
        ? 'card-nav-container--inline'
        : 'card-nav-container--floating';

  const logoInner = <img src={logo} alt={logoAlt} className="card-nav-logo-img" />;

  return (
    <div
      ref={containerRef}
      className={`card-nav-container ${layoutClass} ${className}`.trim()}
    >
      <nav
        ref={navRef}
        className={`card-nav ${isExpanded ? 'open' : ''} ${persistExpanded ? 'persist-expanded' : ''} ${hideTopBar ? 'hide-top-bar' : ''}`}
        style={{ backgroundColor: baseColor }}
      >
        {!hideTopBar && (
        <div className="card-nav-top">
          {!persistExpanded && (
            <div
              className={`hamburger-menu ${isExpanded ? 'open' : ''}`}
              onClick={toggleMenu}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleMenu();
                }
              }}
              role="button"
              aria-label={isExpanded ? 'Close menu' : 'Open menu'}
              tabIndex={0}
              style={{ color: menuColor || 'currentColor' }}
            >
              <div className="hamburger-line" />
              <div className="hamburger-line" />
            </div>
          )}

          <div className="logo-container">
            {logoHref || onLogoClick ? (
              <a
                href={logoHref ?? '#'}
                className="card-nav-logo-link"
                onClick={(e) => {
                  if (onLogoClick) {
                    e.preventDefault();
                    onLogoClick(e);
                  }
                }}
              >
                {logoInner}
              </a>
            ) : (
              logoInner
            )}
          </div>

          <div className="card-nav-top-actions">
            {topBarExtra}
            {!hideCta && (
              <button
                type="button"
                className="card-nav-cta-button"
                style={{ backgroundColor: buttonBgColor, color: buttonTextColor }}
                onClick={onCtaClick}
              >
                {ctaLabel}
              </button>
            )}
          </div>
        </div>
        )}

        <div
          className="card-nav-content"
          aria-hidden={!hideTopBar && !persistExpanded && !isExpanded}
        >
          {(items || []).slice(0, 3).map((item, idx) => (
            <div
              key={`${item.label}-${idx}`}
              className={`nav-card ${selectMode && activeIndex === idx ? 'nav-card--active' : ''}`}
              ref={setCardRef(idx)}
              style={{ backgroundColor: item.bgColor, color: item.textColor }}
              role={selectMode ? 'button' : undefined}
              tabIndex={selectMode ? 0 : undefined}
              onClick={() => handleCardClick(idx, item)}
              onKeyDown={(e) => {
                if (selectMode && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  item.onSelect?.();
                }
              }}
            >
              <div className="nav-card-label">{item.label}</div>
              {item.links && item.links.length > 0 && (
                <div className="nav-card-links">
                  {item.links.map((lnk, i) => (
                    <a
                      key={`${lnk.label}-${i}`}
                      className="nav-card-link"
                      href={lnk.href ?? '#'}
                      aria-label={lnk.ariaLabel ?? lnk.label}
                      onClick={(e) => handleLinkClick(e, lnk)}
                    >
                      <ArrowIcon />
                      {lnk.label}
                    </a>
                  ))}
                </div>
              )}
              {selectMode && (
                <span className="nav-card-select-hint">
                  {activeIndex === idx ? 'Selected' : 'Tap to open'}
                </span>
              )}
            </div>
          ))}
        </div>
      </nav>
    </div>
  );
};

export default CardNav;
